import type { HistoryAction, HistoryEvent } from "@models/reviews";

/**
 * Latest state of one upload, derived from its history events (PBI #781340).
 * `unknown` = the latest action is none of the known ones; the API logs that as an ERROR,
 * the UI shows the raw action name instead of guessing a state.
 */
export type UploadState = "pending" | "superseded" | "cancelled" | "imported" | "failed" | "unknown";

export interface UploadRow {
  uploadKey: string;
  uploadedBy: string;
  uploadedAt: string;
  rows: number | null;
  state: UploadState;
  /** The event that set `state` (who/when, counts, reason). */
  latest: HistoryEvent;
  /** Where the file lives now and its size — the same on every event of one upload. */
  fileKey: string | null;
  size: number | null;
}

const STATE_BY_ACTION: Record<HistoryAction, UploadState> = {
  validated: "pending",
  superseded: "superseded",
  cancelled: "cancelled",
  imported: "imported",
  failed: "failed",
};

function stateOf(action: string): UploadState {
  return Object.prototype.hasOwnProperty.call(STATE_BY_ACTION, action)
    ? STATE_BY_ACTION[action as HistoryAction]
    : "unknown";
}

/** States of uploads that are no longer the file's current upload. */
export const INACTIVE_STATES: ReadonlySet<UploadState> = new Set(["superseded", "cancelled", "failed"]);

/**
 * One row per upload instead of one per event: an upload that was validated and later
 * superseded is a single row "Superseded". `events` come from the API oldest first; rows
 * are returned newest upload first.
 */
export function groupUploads(events: HistoryEvent[]): UploadRow[] {
  const byUpload = new Map<string, HistoryEvent[]>();
  events.forEach((e, i) => {
    const key = e.upload_key ?? `event-${i}`;
    byUpload.set(key, [...(byUpload.get(key) ?? []), e]);
  });

  const rows: UploadRow[] = [];
  for (const [uploadKey, evs] of byUpload) {
    const uploaded = evs.find((e) => e.action === "validated") ?? evs[0];
    const latest = evs[evs.length - 1];
    rows.push({
      uploadKey,
      uploadedBy: uploaded.actor,
      uploadedAt: uploaded.ts,
      rows: uploaded.total_count ?? null,
      state: stateOf(latest.action),
      latest,
      fileKey: latest.file_key,
      size: latest.size,
    });
  }
  return rows.sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
}
